import React, { useContext, useState } from "react";
import { FiKey, FiCopy, FiCheck, FiUnlock } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { AuthContext, AuthContextType } from "../../../contexts/auth-context";
import { openDoor } from "../../../actions/unit-actions";
import SnackbarComponent from "../../../components/Snackbar/Snackbar";
import { useSnack } from "../../../hooks/useSnack";
import "./Access.scss";

function Access() {
  const { user } = useContext<AuthContextType>(AuthContext);
  const { t } = useTranslation();
  const snack = useSnack();
  const [copiedCode, setCopiedCode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);

  const toggleModal = () => setModalOpen(!modalOpen);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleOpenDoor = async () => {
    setIsUnlocking(true);
    try {
      await openDoor();
      snack.setMessage(t("home.doorUnlocked"));
      snack.setSeverity("success");
      snack.onOpen();
    } catch (error: any) {
      snack.setMessage(error.response?.data?.message || t("home.doorUnlockFailed"));
      snack.setSeverity("error");
      snack.onOpen();
    } finally {
      setIsUnlocking(false);
      setModalOpen(false);
    }
  };

  const hasDoorLockCode = user?.doorLockCode;

  return (
    <>
      <div className="access-page dark-theme">
        <div className="access-container">
          <div className="access-card">
            {/* Header */}
            <div className="access-header">
              <div className="access-header__icon">
                <FiKey size={36} />
              </div>
              <h1 className="access-header__title">{t("access.title")}</h1>
              <p className="access-header__subtitle">
                {t("access.subtitle")}
              </p>
            </div>

            {hasDoorLockCode ? (
              <>
                {/* Door Lock Code Display */}
                <div className="access-code">
                  <p className="access-code__label">{t("access.yourDoorLockCode")}</p>
                  <p className="access-code__value">{user.doorLockCode}</p>
                  <button
                    className={`access-code__copy-btn ${copiedCode ? 'access-code__copy-btn--copied' : ''}`}
                    onClick={() => copyToClipboard(user.doorLockCode)}
                  >
                    {copiedCode ? (
                      <>
                        <FiCheck size={16} /> {t("common.copied")}!
                      </>
                    ) : (
                      <>
                        <FiCopy size={16} /> {t("access.copyCode")}
                      </>
                    )}
                  </button>
                </div>

                {/* Remote Unlock Section */}
                <div className="access-unlock">
                  <p className="access-unlock__label">{t("access.orUnlockRemotely")}</p>
                  <button
                    className="access-unlock__button"
                    onClick={toggleModal}
                    disabled={isUnlocking}
                  >
                    {isUnlocking ? (
                      <>
                        <span className="loading-spinner" /> {t("home.unlocking")}
                      </>
                    ) : (
                      <>
                        <FiUnlock size={22} /> {t("home.openDoor")}
                      </>
                    )}
                  </button>
                  <p className="access-unlock__hint">
                    {t("access.unlockHint")}
                  </p>
                </div>
              </>
            ) : (
              <div className="access-empty">
                <div className="access-empty__icon">
                  <FiKey size={28} />
                </div>
                <h2 className="access-empty__title">{t("access.accessCodeNotAvailable")}</h2>
                <p className="access-empty__text">
                  {t("access.accessCodeNotAvailableText")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Door Unlock Confirmation Modal */}
      {modalOpen && (
        <div className="door-confirm-modal">
          <div className="door-confirm-modal__backdrop" onClick={toggleModal} />
          <div className="door-confirm-modal__content">
            <div className="door-confirm-modal__icon">
              <FiUnlock size={32} />
            </div>
            <h3 className="door-confirm-modal__title">{t("doorConfirm.title")}</h3>
            <p className="door-confirm-modal__text">
              {t("doorConfirm.message")}
            </p>
            <div className="door-confirm-modal__actions">
              <button
                className="door-confirm-modal__btn door-confirm-modal__btn--cancel"
                onClick={toggleModal}
                disabled={isUnlocking}
              >
                {t("common.cancel")}
              </button>
              <button
                className="door-confirm-modal__btn door-confirm-modal__btn--confirm"
                onClick={handleOpenDoor}
                disabled={isUnlocking}
              >
                {isUnlocking ? t("home.unlocking") : t("doorConfirm.yesUnlock")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar for notifications */}
      <SnackbarComponent
        isOpen={snack.isOpen}
        severity={snack.severity}
        onClose={snack.onClose}
        message={snack.message}
      />
    </>
  );
}

export default Access;
